<template>
  <div v-if="personaliseableData" :class="[{ 'owc-simple-teaser--dark': isDarkMode }, 'owc-simple-teaser']">
    <template v-if="isEditMode">
      <ValidationBoxWrapper>
        <slot slot="validation-box" name="validation-box" />
      </ValidationBoxWrapper>
      <SlotContainer
        v-if="shouldShowMedia"
        refName="slot-simpleTeaserItem"
        class="owc-simple-teaser__slider-author"
        isReactive="true"
        :slot-component-data="JSON.stringify({ darkMode: isDarkMode })"
      >
        <slot></slot>
      </SlotContainer>
      <div class="owc-simple-teaser__slider-author">
        <SlotContainer
          v-for="(itemId, index) in visibleItems"
          :key="index"
          refName="slot-simpleTeaserItem"
          :slotName="itemId"
          :slot-component-data="JSON.stringify({ darkMode: isDarkMode })"
        >
          <slot :slot="itemId" :name="itemId" />
        </SlotContainer>
      </div>
    </template>
    <!--ATTENTION: This implementation will no longer work if workbench is updated to version 7
      since they replaced the class wb-slider__item with an actual web component. We tried ato make it
      work for that version as well but we're pretty sure that to make this a full FE solution we would need
      functional components or a similar complex solution since the items that come from the BE via slot, need
      to dynamically be wrapped in a wb-slider-item component.
    -->
    <template v-else>
      <wb-slider
        v-show="shouldShowMedia"
        show-dot-navigation
        show-arrow-navigation
        grid-gutter
        class="owc-simple-teaser__slider"
        ref="wbSlider"
        :theme="theme"
        :items-per-page="itemsPerPage"
      >
        <SlotContainer
          v-for="(itemId, index) in visibleItems"
          :key="index"
          class="owc-simple-teaser__item wb-slider__item wb-grid-col-mq6-3 wb-grid-col-mq4-4 wb-grid-col-mq3-6 wb-grid-col-mq1-12 owc-wb-grid-col-mq-2-3-midpoint"
          refName="slot-simpleTeaserItem"
          isReactive="true"
          :slotName="itemId"
          :slot-component-data="JSON.stringify({ darkMode: isDarkMode })"
        >
          <slot :slot="itemId" :name="itemId" />
        </SlotContainer>
      </wb-slider>
    </template>
    <div v-if="footnotes" class="wb-grid-container owc-simple-teaser__footnotes">
      <globalelem-footnote-component
        :module-id="moduleId"
        :theme="isDarkMode ? 'dark' : 'light'"
      ></globalelem-footnote-component>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * `SimpleTeaser` - Simple Teaser Component.
 * This is the wrapper component- This component uses workbench wb-content-slider.
 * {@link https://wb2.oneweb.mercedes-benz.com/developer/web-components/content-slider}
 * This component accepts a datajson as attribute to change the theme between light and dark {theme: 'light' | 'dark'}
 * As slot, SimpleTeaser uses SimpleTeaserItem (owc-simple-teaser-item) which will be the slide for the wb-content-slider.
 * SimpleTeaserItem should always have the classes:
 * - wb-grid-col-mq6-3 wb-grid-col-mq4-4 wb-grid-col-mq3-6 wb-grid-col-mq1-12 owc-wb-grid-col-mq-2-3-midpoint
 * - owc-simple-teaser-item
 * - wb-content-slider__item
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { SimpleTeaserComponent } from '@owc-packages/SimpleTeaser/src/SimpleTeaser';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import {
  OWCPersonalisationState,
  OWC_PERSONALISATION_CONNECTION,
} from '@owc-utils/SeamlessStore/connections';
import { getMediaQueryService, WB_RESIZE_EVENT_NAME, Breakpoints } from '@owc-utils/MediaQueryService';
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import { initializeStore } from '@seamless/store';
import { filterVisibleSlots } from '@owc-utils/Slot/SlotAttributes';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';

const MAX_PAGE_ITEMS = 4;
const sliderItemsPerMQ = new Map([
  [Breakpoints.mq1, 1],
  [Breakpoints.mq2, 1],
  [Breakpoints.mq3, 2],
  [Breakpoints.mq4, 3],
  [Breakpoints.mq5, MAX_PAGE_ITEMS],
]);
export default (ComponentInitializeDispatcher as SimpleTeaserComponent).extend({
  name: 'owc-simple-teaser',
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent, Personalisation],
  mounted() {
    this.namedSlots = filterVisibleSlots(this.$slots);
    const store = initializeStore();

    this.subscription = store.subscribe(OWC_PERSONALISATION_CONNECTION, (state: OWCPersonalisationState) =>
      this.handlePersonalisationItems(state),
    );
    this.shouldShowMedia = true;
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    this.wbMediaQueryService.observe(true);
  },
  beforeDestroy() {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    this.subscription?.unsubscribe();
  },
  data() {
    return {
      shouldShowMedia: false,
      namedSlots: [],
      itemsPerPage: MAX_PAGE_ITEMS,
      wbMediaQueryService: getMediaQueryService(),
    };
  },
  computed: {
    isDarkMode: function() {
      return Boolean(this.personaliseableData.darkMode);
    },
    theme: function() {
      return this.isDarkMode ? 'dark' : 'light';
    },
    visibleItems() {
      return this.namedSlots.filter(el => el.isVisible).map(el => el.name);
    },
    footnotes() {
      return getLocalFootnotes(this.personaliseableData);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    handlePersonalisationItems(state: OWCPersonalisationState): void {
      const personalisationVisibleItems =
        state.find(item => item.componentId === this.componentId)?.itemsToShow ?? [];
      this.updateVisibleItems(personalisationVisibleItems);
    },
    updateVisibleItems(componentIds: Array<string>): void {
      componentIds.forEach(idAdded => {
        this.namedSlots.forEach(slot => {
          if (slot.name === idAdded) {
            slot.isVisible = true;
            (this.$refs.wbSlider as HTMLWbSliderElement).update();
          }
        });
      });
    },
    resizeEventHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.itemsPerPage = sliderItemsPerMQ.get(resizeEvent.detail.current) || MAX_PAGE_ITEMS;
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/grid';
@import '@owc-styles/utils/rte';

$simpleteaserModuleName: 'owc-simple-teaser';

.#{$simpleteaserModuleName} {
  .#{$simpleteaserModuleName} {
    &__slider {
      @include wb-content-width;

      padding-bottom: var(--wb-spacing-m);

      ::slotted(.webcomponent) {
        height: 100%;
      }
    }
    &__item {
      margin-bottom: var(--wb-spacing-3xs);

      &[class*='wb-grid-col-mq'] {
        padding: var(--wb-spacing-3xs);
      }
    }
  }
  &--dark {
    background-color: var(--wb-black);
  }
}
wb-dot-navigation {
  margin-bottom: var(--dot-nav-offset);
}
</style>

<!--AUTHOR CSS -->
<style lang="scss">
/* stylelint-disable */
.owc-simple-teaser__slider-author {
  display: grid;
  gap: var(--wb-grid-gutter-width);
  max-width: 1680px;
  width: var(--wb-grid-content-width);
  margin: var(--wb-spacing-xs) auto;

  $mq-cols: (
    mq1: 1,
    mq2: 1,
    mq3: 2,
    mq4: 3,
    mq5: 3,
    mq6: 4,
  );

  @each $mq in map-keys($wb-breakpoints) {
    @if ($mq == mq1) {
      grid-template-columns: repeat(map-get($mq-cols, $mq), 1fr);
    } @else {
      @include breakpoint-from($mq) {
        grid-template-columns: repeat(map-get($mq-cols, $mq), 1fr);
      }
    }
  }

  ::slotted(.webcomponent) {
    height: 100%;
  }

  .owc-simple-teaser__item {
    margin: var(--wb-spacing-xs);
  }
}
</style>
